import { includes } from 'lodash';

import { Action } from '../actions';
import {
    CHANGE_COLUMNS_CHECKED_FROM_COLUMNS_SETTINGS_EDITOR,
    CHANGE_COLUMNS_ORDER_FROM_COLUMNS_SETTINGS_EDITOR,
    CHANGE_GOALS_CHECKED_FROM_COLUMNS_SETTINGS_EDITOR,
    CHANGE_GOALS_ORDER_FROM_COLUMNS_SETTINGS_EDITOR,
    ChangeColumnsCheckedFromColumnsSettingsEditor,
    ChangeColumnsOrderFromColumnsSettingsEditor,
    ChangeGoalsCheckedFromColumnsSettingsEditor,
    ChangeGoalsOrderFromColumnsSettingsEditor,
    CLOSE_COLUMNS_SETTINGS_EDITOR,
    OPEN_COLUMNS_SETTINGS_EDITOR,
    SAVE_COLUMNS_SETTINGS_EDITOR,
    SELECT_MENU_ITEM_COLUMNS_SETTINGS_EDITOR,
    TOGGLE_COMPACT_VIEW_SETTINGS_EDITOR,
    TOGGLE_ONLY_FAVORITE_SETTINGS_EDITOR,
    TOGGLE_SHOW_TOTAL_SETTINGS_EDITOR,
    UPDATE_GOALS_SETTINGS_COLUMNS_SETTINGS_EDITOR
} from '../actions/columnsSettingsEditor';
import { GridCampaignColumnKey } from '../declarations/campaign/campaign';
import { getColumnsSettings } from '../declarations/common';
import { PhraseColumnKey } from '../declarations/phrase/phrase';
import { StatKey } from '../declarations/stats/stats';
import {
    getColumnSettingsWithUpdatedOrder,
    getColumnsVisibilitySettings,
    getGoalsSettingsWithUpdatedOrder,
    getGoalsVisibilitySettings
} from '../service/ActionHelpers';
import { getCommonSettings } from '../service/DeclarationHelper';
import { ColumnsSettingsEditorState } from '../typings/columnsSettingsEditor';
import { feedFilters, retargetings } from '../typings/common';
import { CampaignsCalcTypeFilter } from '../typings/domain';
import { State } from '../typings/state';

export const ColumnsSettingsEditorInitialState: ColumnsSettingsEditorState = {
    commonSettings: getCommonSettings(),
    goalsSettings: [],
    columnsSettings: getColumnsSettings(),
    isOpened: false,
    selectedMenuItem: ''
};

const changeColumnsOrderFromColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState,
    action: ChangeColumnsOrderFromColumnsSettingsEditor
): ColumnsSettingsEditorState => {
    const settings = getColumnSettingsWithUpdatedOrder(
        state.columnsSettings,
        action.listKeys,
        action.view
    );

    return {
        ...state,
        columnsSettings: settings
    };
};

const changeColumnsCheckedFromColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState,
    action: ChangeColumnsCheckedFromColumnsSettingsEditor,
    fullState: State
): ColumnsSettingsEditorState => {
    const { campaignsCalcTypeFilter } = fullState.grid;
    const { isCpmCampaignsEnabled } = fullState.commonData.features;

    const hasOnlyCpmCampaigns =
        campaignsCalcTypeFilter === CampaignsCalcTypeFilter.CPM &&
        isCpmCampaignsEnabled;

    const settings = getColumnsVisibilitySettings(
        state.columnsSettings,
        action.listKeys,
        action.view
    );

    const columnKeysForIgnoreInCpm = [
        PhraseColumnKey.autobudgetPriority,
        PhraseColumnKey.trafficForecast,
        PhraseColumnKey.coverage,
        StatKey.STAT_AVG_CLICK_COST,
        GridCampaignColumnKey.platform
    ];

    const columnKeysForIgnoreInCpc = [StatKey.STAT_CPM_PRICE];

    /*
        Для того, чтобы при изменении отображения колонок CPM кампаний не изменялось
        состояние колонок, которые только в CPC и наоборот.
     */
    if (action.view !== retargetings && action.view !== feedFilters) {
        Object.keys(state.columnsSettings[action.view].columnViewInfo).forEach(
            columnKey => {
                if (
                    (includes(columnKeysForIgnoreInCpc, columnKey) &&
                        !hasOnlyCpmCampaigns) ||
                    (includes(columnKeysForIgnoreInCpm, columnKey) &&
                        hasOnlyCpmCampaigns)
                ) {
                    settings[action.view].columnViewInfo[columnKey].hidden =
                        state.columnsSettings[action.view].columnViewInfo[
                            columnKey
                        ].hidden;
                }
            }
        );
    }

    return {
        ...state,
        columnsSettings: settings
    };
};

const toggleShowTotalInColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState
): ColumnsSettingsEditorState => {
    const { commonSettings } = state;

    return {
        ...state,
        commonSettings: {
            ...commonSettings,
            isGridTotalBoxVisible: !commonSettings.isGridTotalBoxVisible
        }
    };
};

const toggleCompactViewInColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState
): ColumnsSettingsEditorState => {
    const { commonSettings } = state;

    return {
        ...state,
        commonSettings: {
            ...commonSettings,
            isCompactViewEnabled: !commonSettings.isCompactViewEnabled
        }
    };
};

const toggleOnlyFavoriteInColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState
): ColumnsSettingsEditorState => {
    const { commonSettings } = state;

    return {
        ...state,
        commonSettings: {
            ...commonSettings,
            isOnlyFavoriteCampaignsVisible: !commonSettings.isOnlyFavoriteCampaignsVisible
        }
    };
};

const changeGoalsOrderFromColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState,
    action: ChangeGoalsOrderFromColumnsSettingsEditor
): ColumnsSettingsEditorState => {
    if (!state.goalsSettings) {
        return state;
    }

    const settings = getGoalsSettingsWithUpdatedOrder(
        state.goalsSettings,
        action.listKeys
    );

    return {
        ...state,
        goalsSettings: settings
    };
};

const changeGoalsCheckedFromColumnsSettingsEditor = (
    state: ColumnsSettingsEditorState,
    action: ChangeGoalsCheckedFromColumnsSettingsEditor
): ColumnsSettingsEditorState => {
    if (!state.goalsSettings) {
        return state;
    }

    const settings = getGoalsVisibilitySettings(
        state.goalsSettings,
        action.listKeys
    );

    return {
        ...state,
        goalsSettings: settings
    };
};

export function columnsSettingsEditor(
    state: ColumnsSettingsEditorState = ColumnsSettingsEditorInitialState,
    action: Action,
    fullState: State
): ColumnsSettingsEditorState {
    switch (action.type) {
        case OPEN_COLUMNS_SETTINGS_EDITOR:
            const columnsSettingsEditorData = {
                ...ColumnsSettingsEditorInitialState,
                commonSettings: {
                    ...action.commonSettings
                },
                goalsSettings: action.goalsSettings && [
                    ...action.goalsSettings
                ],
                columnsSettings: {
                    ...ColumnsSettingsEditorInitialState.columnsSettings,
                    ...action.columnsSettings
                }
            };

            return {
                ...state,
                ...columnsSettingsEditorData,
                selectedMenuItem: action.view,
                isOpened: true
            };
        case CLOSE_COLUMNS_SETTINGS_EDITOR:
            return {
                ...state,
                isOpened: false
            };
        case SELECT_MENU_ITEM_COLUMNS_SETTINGS_EDITOR:
            return {
                ...state,
                selectedMenuItem: action.value
            };
        case CHANGE_COLUMNS_ORDER_FROM_COLUMNS_SETTINGS_EDITOR:
            return changeColumnsOrderFromColumnsSettingsEditor(state, action);
        case CHANGE_COLUMNS_CHECKED_FROM_COLUMNS_SETTINGS_EDITOR:
            return changeColumnsCheckedFromColumnsSettingsEditor(
                state,
                action,
                fullState
            );
        case TOGGLE_SHOW_TOTAL_SETTINGS_EDITOR:
            return toggleShowTotalInColumnsSettingsEditor(state);
        case TOGGLE_COMPACT_VIEW_SETTINGS_EDITOR:
            return toggleCompactViewInColumnsSettingsEditor(state);
        case TOGGLE_ONLY_FAVORITE_SETTINGS_EDITOR:
            return toggleOnlyFavoriteInColumnsSettingsEditor(state);
        case CHANGE_GOALS_ORDER_FROM_COLUMNS_SETTINGS_EDITOR:
            return changeGoalsOrderFromColumnsSettingsEditor(state, action);
        case CHANGE_GOALS_CHECKED_FROM_COLUMNS_SETTINGS_EDITOR:
            return changeGoalsCheckedFromColumnsSettingsEditor(state, action);
        case SAVE_COLUMNS_SETTINGS_EDITOR:
            return {
                ...state,
                isOpened: false
            };
        case UPDATE_GOALS_SETTINGS_COLUMNS_SETTINGS_EDITOR:
            return {
                ...state,
                goalsSettings: action.goalsSettings
            };
        default:
            return state;
    }
}
